/**
 * Money and packaging math for the producer cabinet.
 *
 * MONEY IS NEVER A JS NUMBER. Over the wire it is a decimal string (CLAUDE.md), and in here it
 * stays a `Decimal` (decimal.js) until the moment it is formatted for display. `0.1 + 0.2` is
 * the reason: a float round-trip loses a cent per line, and then Merdan gets to argue about it
 * with an accountant.
 *
 * `@most/cart-math` owns the truck-fill and cart pricing math shared with the buyer SPA (and
 * cross-checked against Go via a shared fixture file). The producer cabinet needs NONE of that:
 * truck-fill is a whole-order aggregate and therefore FORBIDDEN on producer surfaces
 * (docs/04 §5). What it needs is the packaging chain and base-price normalization below, which
 * are producer-side only. Importing the cart's truck-fill helpers here would be the first step
 * toward rendering one.
 */
import Decimal from 'decimal.js';
import type { CapacityUnit, PackagingChain, PriceUnit, QtyUnit } from './contract';

// 6 dp on per-piece values, matching numeric(14,6) server-side.
Decimal.set({ precision: 28, rounding: Decimal.ROUND_HALF_UP });

/** The packaging fields, before they are known to be complete (wizard step 2 in progress). */
export interface PartialChain {
  pieces_per_pack?: number | string;
  packs_per_box?: number | string | null;
  boxes_per_pallet?: number | string;
  pallets_per_truck?: number | string | null;
}

/** Reference truck (server config TRUCK_PALLETS) — used when pallets_per_truck is not stated. */
const TRUCK_PALLETS = 33;

const int = (v: number | string | null | undefined): number => {
  const n = typeof v === 'string' ? Number.parseInt(v, 10) : v;
  return Number.isFinite(n) && (n as number) > 0 ? (n as number) : 0;
};

/**
 * pieces_in(unit) — the packaging chain multipliers (docs/04 §2.1).
 *
 *   piece  → 1
 *   pack   → pieces_per_pack
 *   box    → pieces_in(pack) × packs_per_box
 *   pallet → pieces_in(box)  × boxes_per_pallet
 *   truck  → pieces_in(pallet) × pallets_per_truck
 *
 * packs_per_box and pallets_per_truck are optional and default to 1 and 33 (as on the server).
 * Returns 0 when the chain is incomplete — callers treat 0 as "cannot compute yet" and must not
 * divide by it.
 */
export function piecesIn(
  unit: PriceUnit | QtyUnit | CapacityUnit,
  chain: PartialChain | PackagingChain,
): number {
  const perPack = int(chain.pieces_per_pack);
  const perBox = int(chain.packs_per_box) || 1; // optional: unstated = one pack per box
  const perPallet = int(chain.boxes_per_pallet);
  const perTruck = int(chain.pallets_per_truck) || TRUCK_PALLETS; // optional

  switch (unit) {
    case 'piece':
      return 1;
    case 'pack':
      return perPack;
    case 'box':
      return perPack * perBox;
    case 'pallet':
      return perPack * perBox * perPallet;
    case 'truck':
      return perPack * perBox * perPallet * perTruck;
    default:
      return 0;
  }
}

/**
 * Base price normalization, producer side (docs/04 §2.2):
 *
 *   producer_price_per_piece = producer_price_value ÷ pieces_in(producer_price_unit)
 *
 * This is the ONLY price math the producer cabinet performs. The result is the producer's own
 * FCA price expressed per piece — their data, shown back to them. It is NOT and can never be
 * derived into a selling price; we do not have one.
 *
 * Returns null when the chain is incomplete or the value is not a positive decimal, so the
 * wizard's live preview can show a dash instead of `NaN` or `Infinity`.
 */
export function normalizeToPerPiece(
  value: string | number | undefined,
  unit: PriceUnit | undefined,
  chain: PartialChain,
): Decimal | null {
  if (value === undefined || value === '' || !unit) return null;

  let v: Decimal;
  try {
    v = new Decimal(String(value).replace(',', '.'));
  } catch {
    return null;
  }
  if (!v.isFinite() || v.lte(0)) return null;

  const pieces = piecesIn(unit, chain);
  if (pieces <= 0) return null;

  return v.div(pieces);
}

/** Per-piece display: 6 dp, matching the server's numeric(14,6). */
export function formatPerPiece(d: Decimal | null): string {
  if (!d) return '—';
  return `$${d.toFixed(6)}`;
}

/**
 * Totals display: 2 dp, half-up, `$1,234.56`. USD only — there is no currency column anywhere
 * in the system (CLAUDE.md), so the symbol is hardcoded rather than locale-derived.
 */
export function formatUsd(value: string | number | Decimal | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  let d: Decimal;
  try {
    d = value instanceof Decimal ? value : new Decimal(String(value));
  } catch {
    return '—';
  }
  if (!d.isFinite()) return '—';

  const fixed = d.toFixed(2, Decimal.ROUND_HALF_UP);
  const [whole, frac] = fixed.split('.');
  const sign = whole.startsWith('-') ? '-' : '';
  const digits = sign ? whole.slice(1) : whole;
  // Thin-space grouping reads correctly in RU and EN alike.
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${sign}$${grouped}.${frac}`;
}

/** Weights as `12 340 kg` (docs/06 §7). */
export function formatKg(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  let d: Decimal;
  try {
    d = new Decimal(String(value));
  } catch {
    return '—';
  }
  if (!d.isFinite()) return '—';

  // Show up to 3 dp, but drop trailing zeros: 0.25 kg stays 0.25, 12340 stays 12 340.
  const fixed = d.toDecimalPlaces(3, Decimal.ROUND_HALF_UP).toString();
  const [whole, frac] = fixed.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${grouped}${frac ? `.${frac}` : ''} кг`;
}

/** Plain integer grouping for piece counts (`86 400 штук`). */
export function formatInt(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return String(Math.trunc(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/**
 * The live summary line for wizard step 2: "1 фура = N палет = N упаковок = N штук".
 * Returns null while the chain is incomplete, so the UI shows a hint instead of a broken chain.
 */
export interface ChainSummary {
  pallets: number;
  boxes: number;
  packs: number;
  pieces: number;
}

export function summarizeChain(chain: PartialChain): ChainSummary | null {
  const perPack = int(chain.pieces_per_pack);
  const perBox = int(chain.packs_per_box) || 1; // optional: unstated = one pack per box
  const perPallet = int(chain.boxes_per_pallet);
  const perTruck = int(chain.pallets_per_truck) || TRUCK_PALLETS; // optional
  if (!perPack || !perBox || !perPallet || !perTruck) return null;

  return {
    pallets: perTruck,
    boxes: perTruck * perPallet,
    packs: perTruck * perPallet * perBox,
    pieces: perTruck * perPallet * perBox * perPack,
  };
}

/** Absolute dates in tables, per docs/06 §8 (`Intl.DateTimeFormat`, locale-aware). */
export function formatDate(iso: string | null | undefined, locale = 'ru'): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(locale === 'tk' ? 'ru' : locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

export function formatDateTime(iso: string | null | undefined, locale = 'ru'): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(locale === 'tk' ? 'ru' : locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}
